import React, { useCallback, useMemo, useState } from 'react';
import {
	Card,
	Col,
	Collapse,
	Row,
	Select,
	Spin,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import ScoresTable from './ScoresTable';
import { useGetProgramScoresQuery } from '../../redux/api/manufacturing';
import {
	PROGRAM_ISSUE_CAUGHT_PHASE_LABELS,
	PROGRAM_ISSUE_SEVERITY_LABELS,
	PROGRAM_SCORE_WINDOW_LABELS,
	PROGRAM_TYPE_LABELS,
} from '../../interfaces/CamProgramming.interface';
import type {
	ProgramIssueCaughtPhase,
	ProgramIssueSeverity,
	ProgramScoreConfig,
	ProgramScoreWindow,
	ProgramType,
} from '../../interfaces/CamProgramming.interface';

const { Paragraph, Text, Title } = Typography;

const WINDOW_OPTIONS = (
	Object.keys(PROGRAM_SCORE_WINDOW_LABELS) as ProgramScoreWindow[]
).map((value) => ({ value, label: PROGRAM_SCORE_WINDOW_LABELS[value] }));

const PROGRAM_TYPE_OPTIONS = (
	Object.keys(PROGRAM_TYPE_LABELS) as ProgramType[]
).map((value) => ({ value, label: PROGRAM_TYPE_LABELS[value] }));

const SEVERITY_ORDER: ProgramIssueSeverity[] = [
	'low',
	'medium',
	'high',
	'critical',
	'negligent',
];

const PHASE_ORDER: ProgramIssueCaughtPhase[] = [
	'review',
	'secondary_review',
	'production',
	'qc',
	'delivery',
	'shipping',
	'customer',
];

/**
 * Who each caught phase leaves answerable. This is what the phase decides now
 * that it no longer scales the charge — it mirrors `charges_role` on the
 * server, where a review is charged only for what got past its own gate.
 */
const CHARGED_BY_PHASE: Record<ProgramIssueCaughtPhase, string> = {
	review: 'At fault only',
	secondary_review: 'At fault + first review',
	production: 'At fault + both reviews',
	qc: 'At fault + both reviews',
	delivery: 'At fault + both reviews',
	shipping: 'At fault + both reviews',
	customer: 'At fault + both reviews',
};

const Metric: React.FC<{
	label: string;
	value: React.ReactNode;
	caption?: string;
	hint?: string;
}> = ({ label, value, caption, hint }) => (
	<div className="cam-metric">
		<div className="cam-metric__head">
			<span className="cam-metric__label">
				{label}
				{hint && (
					<Tooltip title={hint}>
						<QuestionCircleOutlined className="cam-metric__hint" />
					</Tooltip>
				)}
			</span>
		</div>
		<div className="cam-metric__value">{value}</div>
		{caption && <span className="cam-metric__caption">{caption}</span>}
	</div>
);

/** Trims the trailing zeros a configured decimal arrives with. */
const formatPoints = (value: number) => Number(value.toFixed(2)).toString();

/** Opportunities behind most of the worked examples. */
const EXAMPLE_PROGRAMS = 20;

/** A record thin enough that the score is flagged rather than trusted. */
const THIN_RECORD_PROGRAMS = 4;

/** Catches behind the credit examples. */
const EXAMPLE_CATCHES = 3;

/**
 * A deliberately bad window: something small on every single program, with a
 * tail of serious ones on top. Held as issue counts rather than points so the
 * example reprices itself when the severity weights are retuned.
 */
const HEAVY_RECORD_ISSUES: Partial<Record<ProgramIssueSeverity, number>> = {
	low: EXAMPLE_PROGRAMS,
	medium: 6,
	high: 4,
	critical: 2,
};

/**
 * The published formula applied to a hypothetical record, so the worked example
 * moves with the configured weights instead of quoting a number that goes stale
 * the moment someone retunes them. Mirrors `score_for` on the server, including
 * the floor that stops credits reading as better than a clean record.
 */
const exampleScore = (
	config: ProgramScoreConfig,
	demerits: number,
	opportunities: number,
	credits = 0
) =>
	Math.max(
		0,
		100 -
			(config.score_points_per_demerit * Math.max(0, demerits - credits)) /
				opportunities
	);

/** Live reference tables for how issues are priced and attributed. */
const ScoringReferenceCards: React.FC<{ config: ProgramScoreConfig }> = ({
	config,
}) => (
	<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
		<Col xs={24} md={12}>
			<Card
				size="small"
				title="Severity weight"
				className="cam-score-reference-card"
			>
				<Table
					className="cam-entry-table"
					size="small"
					pagination={false}
					rowKey="severity"
					dataSource={SEVERITY_ORDER.map((severity) => ({
						severity,
						weight: config.severity_weights[severity],
					}))}
					columns={[
						{
							title: 'Severity',
							dataIndex: 'severity',
							render: (severity: ProgramIssueSeverity) =>
								PROGRAM_ISSUE_SEVERITY_LABELS[severity],
						},
						{
							title: 'Demerit points',
							dataIndex: 'weight',
							align: 'right',
							render: (weight: number) => formatPoints(weight),
						},
					]}
				/>
			</Card>
		</Col>
		<Col xs={24} md={12}>
			<Card
				size="small"
				title="Who is charged"
				className="cam-score-reference-card"
			>
				<Table
					className="cam-entry-table"
					size="small"
					pagination={false}
					rowKey="phase"
					dataSource={PHASE_ORDER.map((phase) => ({
						phase,
						charged: CHARGED_BY_PHASE[phase],
					}))}
					columns={[
						{
							title: 'Caught in',
							dataIndex: 'phase',
							render: (phase: ProgramIssueCaughtPhase) =>
								PROGRAM_ISSUE_CAUGHT_PHASE_LABELS[phase],
						},
						{
							title: 'Charged in full',
							dataIndex: 'charged',
						},
					]}
				/>
			</Card>
		</Col>
	</Row>
);

/** A per-program rate expressed against the allowance, which is the yardstick. */
const timesAllowance = (config: ProgramScoreConfig, perProgram: number) =>
	config.allowance_per_program > 0
		? `${(perProgram / config.allowance_per_program).toFixed(1)}×`
		: '—';

interface WorkedExample {
	key: string;
	record: string;
	rule: string;
	opportunities: number;
	demerits: number;
	credits?: number;
}

/**
 * One hypothetical record per rule, priced off the live config so a retune
 * moves the examples instead of leaving the copy quietly wrong. Read together
 * they cover every way a charge can land: in full, redirected, waived, or not
 * at all.
 */
const workedExamples = (config: ProgramScoreConfig): WorkedExample[] => {
	const high = config.severity_weights.high;
	const critical = config.severity_weights.critical;
	const negligent = config.severity_weights.negligent;
	const onTarget = config.allowance_per_program * EXAMPLE_PROGRAMS;
	const creditPerHigh = high * config.catch_credit_fraction;
	const catchCredits = EXAMPLE_CATCHES * creditPerHigh;

	const everyProgramLow = config.severity_weights.low * EXAMPLE_PROGRAMS;
	const heavyDemerits = SEVERITY_ORDER.reduce(
		(total, severity) =>
			total +
			(HEAVY_RECORD_ISSUES[severity] ?? 0) * config.severity_weights[severity],
		0
	);

	return [
		{
			key: 'clean',
			record: 'A programmer with nothing charged against them.',
			rule: 'Everyone starts at 100 and stays there until an issue lands.',
			opportunities: EXAMPLE_PROGRAMS,
			demerits: 0,
		},
		{
			key: 'on-target',
			record: `A programmer carrying ${formatPoints(
				onTarget
			)} demerit points across the window.`,
			rule: `Exactly the ${formatPoints(
				config.allowance_per_program
			)} per program allowance — this is what the ${
				config.target_score
			} target means in practice.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: onTarget,
		},
		{
			key: 'caught-in-review',
			record: 'A programmer whose high-severity issue was caught in first review.',
			rule: `Charged the full ${formatPoints(
				high
			)} points. Being caught early does not discount an issue; it only limits who else answers for it.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: high,
		},
		{
			key: 'reviewer-caught-it',
			record: 'The reviewer who caught that same issue.',
			rule: 'A review is never charged for what it stopped, because stopping it is the job.',
			opportunities: EXAMPLE_PROGRAMS,
			demerits: 0,
		},
		{
			key: 'escaped',
			record:
				'Any one of the three people on a program whose high-severity issue reached production.',
			rule: `The programmer, the first review and secondary review are each charged the same ${formatPoints(
				high
			)} points, so one part costs the shop ${formatPoints(high * 3)}.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: high,
		},
		{
			key: 'fault-moved',
			record:
				'The programmer on a program whose issue was logged against the first reviewer.',
			rule: 'Naming a reviewer moves the whole charge. Nobody who signed off before the change pays for it.',
			opportunities: EXAMPLE_PROGRAMS,
			demerits: 0,
		},
		{
			key: 'training',
			record:
				'The programmer on a critical issue flagged as insufficient training.',
			rule: config.training_excuses_reviewers
				? 'A training gap clears everyone who would have been charged, though the issue still counts in the shop score.'
				: 'A training gap clears whoever was at fault. The reviews that missed it are still charged, and the shop still carries the issue.',
			opportunities: EXAMPLE_PROGRAMS,
			demerits: 0,
		},
		{
			key: 'catch-credit',
			record: `A programmer carrying one critical escape who then caught ${EXAMPLE_CATCHES} high-severity issues in review.`,
			rule: `Each catch credits back ${(
				config.catch_credit_fraction * 100
			).toFixed(0)}% of that issue's points — ${formatPoints(
				creditPerHigh
			)} apiece — which nets off the ${formatPoints(critical)} they owe.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: critical,
			credits: catchCredits,
		},
		{
			key: 'credit-capped',
			record: 'A reviewer with nothing charged against them and a long list of catches.',
			rule: 'Credit only pays down demerits already on the record. A spotless one reads 100, never better than 100, so the scale stays anchored to the allowance.',
			opportunities: EXAMPLE_PROGRAMS,
			demerits: 0,
			credits: catchCredits,
		},
		{
			key: 'thin-record',
			record: `A programmer with one critical issue on only ${THIN_RECORD_PROGRAMS} programs.`,
			rule: `Nothing softens this number — ${formatPoints(
				critical
			)} points over ${THIN_RECORD_PROGRAMS} programs is ${timesAllowance(
				config,
				critical / THIN_RECORD_PROGRAMS
			)} the allowance and it reads that way. The row is flagged provisional instead, because ${THIN_RECORD_PROGRAMS} programs is not enough to call it.`,
			opportunities: THIN_RECORD_PROGRAMS,
			demerits: critical,
		},
		{
			key: 'negligent',
			record: 'A programmer with one negligent issue and nothing else.',
			rule: `The heaviest severity on the ladder at ${formatPoints(
				negligent
			)} points. One is enough to move an otherwise spotless record off the target on its own.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: negligent,
		},
		{
			key: 'low-every-program',
			record: `A programmer with one low-severity issue on every one of ${EXAMPLE_PROGRAMS} programs.`,
			rule: `The lightest severity there is, but never going a program without one puts the rate at ${timesAllowance(
				config,
				everyProgramLow / EXAMPLE_PROGRAMS
			)} the allowance. How often counts as much as how bad.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: everyProgramLow,
		},
		{
			key: 'heavy-record',
			record: `A low-severity issue on all ${EXAMPLE_PROGRAMS} programs, plus ${HEAVY_RECORD_ISSUES.medium} medium, ${HEAVY_RECORD_ISSUES.high} high and ${HEAVY_RECORD_ISSUES.critical} critical.`,
			rule: `${formatPoints(heavyDemerits)} points, or ${timesAllowance(
				config,
				heavyDemerits / EXAMPLE_PROGRAMS
			)} the allowance. This is what the bottom of the scale looks like; it reaches 0 at ${formatPoints(
				config.demerits_scoring_zero
			)} points per program.`,
			opportunities: EXAMPLE_PROGRAMS,
			demerits: heavyDemerits,
		},
	];
};

/** The steps of the calculation, plus the rules that redirect a charge. */
const ScoringMethod: React.FC<{ config: ProgramScoreConfig }> = ({ config }) => {
	const exampleDemerits = config.severity_weights.high;
	const examples = workedExamples(config);

	return (
		<>
			<Paragraph>
				Everyone starts at 100 and loses points for the issues charged to them.
				These five steps are the whole calculation.
			</Paragraph>

			<ol className="cam-scoring-steps">
				<li>
					<Text strong>Price each issue.</Text> An issue is worth its severity
					weight — see the Severity weight card below — and nothing scales it.
					Where the issue was caught decides who answers for it, not what it
					costs them. A high-severity issue is{' '}
					{formatPoints(exampleDemerits)} demerit points whether review caught
					it or the customer did.
				</li>
				<li>
					<Text strong>Charge the points to people.</Text> The points go to
					whoever is at fault, which is the programmer unless the issue names a
					reviewer instead, and the same points go to every review the issue
					then got past. An issue that reached production was missed three
					times and is charged three times over, once to each person who could
					have stopped it. A review that caught the issue, or that had already
					finished before the change that introduced it, is charged nothing.
				</li>
				<li>
					<Text strong>Credit whoever caught it.</Text>{' '}
					{config.catch_credit_fraction > 0 ? (
						<>
							Reporting an issue credits{' '}
							{(config.catch_credit_fraction * 100).toFixed(0)}% of its points
							back to you, against the role you held on that program — but
							only when that role is not itself being charged for it, because
							being charged means it got past you. Credit nets off the
							demerits you already carry and stops at zero, so it can pay a
							record back up but never past what a clean one scores.
						</>
					) : (
						<>
							Catch credit is switched off, so reporting an issue does not
							affect the reporter&apos;s score.
						</>
					)}
				</li>
				<li>
					<Text strong>Divide by opportunities.</Text> An opportunity is one
					program a person held one role on, counted over the selected window.
					Each role column divides that role&apos;s points by that role&apos;s
					opportunities; the headline score divides a person&apos;s total points,
					net of every credit they earned, by their total opportunities across
					all three roles — which is how careful reviewing pays down an older
					programming mistake.
				</li>
				<li>
					<Text strong>Subtract from 100.</Text> Each demerit point per
					opportunity costs {config.score_points_per_demerit.toFixed(2)} score
					points, and a score never goes below 0. That rate is what puts an
					average of {formatPoints(config.allowance_per_program)} demerit points
					per program on the {config.target_score} target. Under{' '}
					{config.provisional_program_threshold} opportunities the score is
					flagged provisional — small samples are labelled rather than adjusted.
				</li>
			</ol>

			<Paragraph>
				<Text strong>Fault.</Text> Logging an issue against a reviewer moves the
				charge to that reviewer. The programmer pays nothing, and neither does
				any review that finished before the change; the reviews after it are
				still charged for missing it.
			</Paragraph>

			<Paragraph>
				<Text strong>Insufficient training.</Text> An issue marked insufficient
				training is not charged to whoever is at fault
				{config.training_excuses_reviewers
					? ', nor to the reviewers who missed it'
					: '; the reviewers who missed it are still charged'}
				. The issue stays on the record either way and still counts once in the
				shop score above.
			</Paragraph>

			<Paragraph>
				<Text strong>Getting a score back up.</Text> Nothing ages off a record on
				its own — an issue costs the same however long ago the work was done.
				A score climbs two ways: catching issues, which credits against what you
				owe, and putting clean work behind the issues already there, since every
				further opportunity divides the same points more thinly. Narrowing the
				window at the top of the tab is what takes older work out of view
				altogether.
			</Paragraph>

			<Paragraph style={{ marginBottom: 4 }}>
				<Text strong>Worked examples.</Text> The same formula applied to{' '}
				{examples.length} hypothetical records — one per rule, then a few at the
				extremes. Every number below is computed from the weights currently in
				force, so these move whenever the config does.
			</Paragraph>

			<Table
				className="cam-entry-table"
				size="small"
				pagination={false}
				rowKey="key"
				style={{ marginBottom: 20 }}
				dataSource={examples}
				columns={[
					{
						title: 'The record',
						dataIndex: 'record',
						render: (record: string, row: WorkedExample) => (
							<>
								<div>{record}</div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									{row.rule}
								</Text>
							</>
						),
					},
					{
						title: 'Opportunities',
						dataIndex: 'opportunities',
						align: 'right',
						width: 120,
					},
					{
						title: 'Demerit pts',
						dataIndex: 'demerits',
						align: 'right',
						width: 110,
						render: (value: number) => formatPoints(value),
					},
					{
						title: 'Credit pts',
						dataIndex: 'credits',
						align: 'right',
						width: 100,
						render: (value: number | undefined) =>
							value ? formatPoints(value) : '—',
					},
					{
						title: 'Score',
						key: 'score',
						align: 'right',
						width: 90,
						render: (_: unknown, row: WorkedExample) => (
							<Text strong>
								{exampleScore(
									config,
									row.demerits,
									row.opportunities,
									row.credits ?? 0
								).toFixed(1)}
							</Text>
						),
					},
				]}
			/>

			<Text type="secondary" style={{ fontSize: 12 }}>
				Weights are editable in the Django admin under Program Scoring Config,
				and every change is version-tracked.
			</Text>
		</>
	);
};

export interface ScoresPanelProps {
	/** Discipline the tab opens on; the panel keeps its own selector after that. */
	defaultProgramType?: ProgramType;
}

const DEFAULT_SCORE_WINDOW: ProgramScoreWindow = '90d';

const ScoresPanel: React.FC<ScoresPanelProps> = ({
	defaultProgramType = 'cam',
}) => {
	// Both selectors live in the URL so a reload restores the same view.
	const [searchParams, setSearchParams] = useSearchParams();

	const typeParam = searchParams.get('program_type');
	const programType: ProgramType =
		typeParam && typeParam in PROGRAM_TYPE_LABELS
			? (typeParam as ProgramType)
			: defaultProgramType;

	const windowParam = searchParams.get('window');
	const scoreWindow: ProgramScoreWindow =
		windowParam && windowParam in PROGRAM_SCORE_WINDOW_LABELS
			? (windowParam as ProgramScoreWindow)
			: DEFAULT_SCORE_WINDOW;

	const setScoreParam = useCallback(
		(key: 'program_type' | 'window', value: string) => {
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					next.set(key, value);
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

	const { data, isLoading, isFetching } = useGetProgramScoresQuery({
		program_type: programType,
		window: scoreWindow,
	});

	const selectedRow = useMemo(
		() => data?.results.find((row) => row.user_id === selectedUserId) ?? null,
		[data, selectedUserId]
	);

	/**
	 * One point per ISO week. The fleet line always shows; a clicked person's
	 * line overlays it so an individual can be read against the shop.
	 */
	const chartData = useMemo(() => {
		if (!data) {
			return [];
		}
		const personByWeek = new Map(
			(selectedRow?.weekly ?? []).map((week) => [week.iso_week, week])
		);
		return data.weeks.map((week) => ({
			iso_week: week.iso_week,
			label: dayjs(week.week_start).format('MMM D'),
			programs: week.programs,
			escaped: week.escaped_issues,
			fleet: week.score,
			person: personByWeek.get(week.iso_week)?.score ?? null,
		}));
	}, [data, selectedRow]);

	if (isLoading || !data) {
		return (
			<div className="w-full bg-white border-b border-r border-l p-6">
				<div className="cam-section__placeholder">
					<Spin />
				</div>
			</div>
		);
	}

	const { config, totals } = data;
	const target = config.target_score;

	return (
		<div className="w-full bg-white border-b border-r border-l p-6">
			<div className="cam-grid-toolbar">
				<Select
					value={programType}
					onChange={(value) => setScoreParam('program_type', value)}
					options={PROGRAM_TYPE_OPTIONS}
					style={{ width: 240 }}
				/>
				<Select
					value={scoreWindow}
					onChange={(value) => setScoreParam('window', value)}
					options={WINDOW_OPTIONS}
					style={{ width: 180 }}
				/>
				<Text type="secondary">
					{totals.programs} completed{' '}
					{totals.programs === 1 ? 'program' : 'programs'} ·{' '}
					{totals.people} {totals.people === 1 ? 'person' : 'people'} scored
				</Text>
			</div>

			<Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
				<Col xs={12} md={6}>
					<Metric
						label="Shop score"
						value={totals.score.toFixed(1)}
						caption={`Per program · target ${target}`}
						hint={
							'Every charge an issue produced, divided by the programs ' +
							'completed in this window. An issue that got past both ' +
							'reviews counts three times here, once for each person who ' +
							'could have stopped it, so this reads lower than an ' +
							'individual score the more work escapes. Training-excused ' +
							'issues are included \u2014 the shop absorbed them either way.'
						}
					/>
				</Col>
				<Col xs={12} md={6}>
					<Metric
						label="At or above target"
						value={`${totals.on_target} / ${totals.people}`}
						caption={
							totals.provisional
								? `${totals.provisional} provisional`
								: undefined
						}
					/>
				</Col>
				<Col xs={12} md={6}>
					<Metric
						label="Demerit points charged"
						value={totals.demerits.toFixed(1)}
						caption={
							totals.training_excused_issues
								? `${totals.training_excused_issues} excused as training`
								: `${totals.raw_demerits_per_program.toFixed(
										2
								  )} per program`
						}
					/>
				</Col>
				<Col xs={12} md={6}>
					<Metric
						label="Escapes past review"
						value={data.weeks.reduce(
							(sum, week) => sum + week.escaped_issues,
							0
						)}
						caption="Reached production or later"
					/>
				</Col>
			</Row>

			{chartData.length > 0 && (
				<div style={{ marginBottom: 24 }}>
					<Title level={5} style={{ marginBottom: 4 }}>
						Score by ISO week
					</Title>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Shop-wide score per week, plotted against the target of {target}.
						Click a row below to overlay that person&apos;s weekly scores.
					</Text>
					<ResponsiveContainer width="100%" height={260}>
						<LineChart
							data={chartData}
							margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
						>
							<CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
							<XAxis
								dataKey="label"
								tick={{ fontSize: 12 }}
								label={{
									value: 'Week beginning',
									position: 'insideBottom',
									offset: -4,
									style: { fontSize: 12, fill: '#8c8c8c' },
								}}
							/>
							<YAxis
								domain={[0, 100]}
								tick={{ fontSize: 12 }}
								label={{
									value: 'Score (0–100)',
									angle: -90,
									position: 'insideLeft',
									style: { fontSize: 12, fill: '#8c8c8c' },
								}}
							/>
							<RechartsTooltip
								formatter={(value, name) => [
									typeof value === 'number' ? value.toFixed(1) : '—',
									name,
								]}
								labelFormatter={(label: string, payload) => {
									const point = payload?.[0]?.payload;
									return point
										? `${point.iso_week} (week of ${label}) · ${point.programs} programs`
										: label;
								}}
							/>
							<Legend verticalAlign="top" height={28} />
							<ReferenceLine
								y={target}
								stroke="#52c41a"
								strokeDasharray="4 4"
								label={{
									value: `Target ${target}`,
									position: 'right',
									style: { fontSize: 11, fill: '#52c41a' },
								}}
							/>
							<Line
								type="monotone"
								dataKey="fleet"
								name="Shop"
								stroke="#1677ff"
								strokeWidth={2}
								dot={{ r: 3 }}
							/>
							{selectedRow && (
								<Line
									type="monotone"
									dataKey="person"
									name={selectedRow.full_name}
									stroke="#fa8c16"
									strokeWidth={2}
									dot={{ r: 3 }}
									connectNulls
								/>
							)}
						</LineChart>
					</ResponsiveContainer>
				</div>
			)}

			<ScoresTable
				rows={data.results}
				config={config}
				loading={isFetching}
				selectedUserId={selectedUserId}
				onSelect={setSelectedUserId}
			/>

			<Collapse
				ghost
				style={{ marginTop: 16 }}
				items={[
					{
						key: 'method',
						label: 'How these scores are calculated',
						children: <ScoringMethod config={config} />,
					},
				]}
			/>

			<ScoringReferenceCards config={config} />
		</div>
	);
};

export default ScoresPanel;
